import { useState } from 'react';

import { useAxiosPrivate } from '../../../../hooks';
import { slugify } from '../../../../utils/common';
import { toasts } from 'ui';

type handleExportEntityArgs = {
  entityType: string;
  entityId: string;
  preview?: boolean;
  name?: string;
  isLive?: boolean;
  version?: string;
};

type handleExportRowsArgs = {
  entityId: string;
  type : string;
  name? : string;
  format : string
  version? : string
};

export function useExportEntity() {
  const { axiosPrivate } = useAxiosPrivate();
  const [isLoading, setIsLoading] = useState(false);

  const handleExportEntity = async ({
    entityType,
    entityId,
    preview = false,
    name = '',
    isLive = false,
    version,
  }: handleExportEntityArgs) => {
    try {
      const response = await axiosPrivate.post(`/common/${entityType}/export`, {
        entityId,
        preview,
        isLive,
        version,
      });

      if (preview) {
        return response;
      }

      const jsonString = JSON.stringify(response.data, null, 2);
      // Create a blob with the JSON string
      const blob = new Blob([jsonString], { type: 'application/json' });

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      // Create a link element
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        slugify(`${name ?? entityType}-${new Date().toDateString()}-export`)
      ); // Filename and extension

      // Append the link to the body
      document.body.appendChild(link);
      // Programmatically click the link to trigger the download
      link.click();
      // Remove the link from the document
      document.body.removeChild(link);
    } catch (err) {}
  };

  return {
    isLoading,
    setIsLoading,
    handleExportEntity,
  };
}


export function useExportRows() {
  const { axiosPrivate } = useAxiosPrivate();
  const [isRowsExportLoading, setIsLoading] = useState(false);

  const handleExportRows = async ({
    entityId,
    type,
    name,
    format,
    version
  }: handleExportRowsArgs) => {
    try {
      setIsLoading(true);
      
      const response = await axiosPrivate.post(`/common/rule/export`, {
        entityId,
        type,
        format,
        version
      });

      if(response.data.code === "error_in_export"){
        return;
      }
      // Response data is already in CSV format
      const csvString = response.data;
      
      // Create a blob with the CSV string
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a link element
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        slugify(`${name ?? 'rule-rows'}-${new Date().toDateString()}-export`) + '.csv'
      ); // Filename with .csv extension

      // Append the link to the body
      document.body.appendChild(link);
      
      // Programmatically click the link to trigger the download
      link.click();
      
      // Clean up: remove the link and revoke the URL
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toasts.success('File exported successfully.', 'export-entity-success');
      
    } catch (err) {
      console.error('Export failed:', err);
      // You might want to show a toast notification or handle the error appropriately
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isRowsExportLoading,
    setIsLoading,
    handleExportRows,
  };
}